import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, map, shareReplay, switchMap } from 'rxjs';

import { BaseService } from './base-service';
import { ListWorkPackagePriorities$Params } from '../api/backend-connector/fn/work-package-priorities/list-work-package-priorities';
import { wpPriorityHighIcon } from '../svg/wp-priority-high';
import { wpPriorityHighestIcon } from '../svg/wp-priority-highest';
import { wpPriorityLowIcon } from '../svg/wp-priority-low';
import { wpPriorityLowestIcon } from '../svg/wp-priority-lowest';
import { wpPriorityMediumIcon } from '../svg/wp-priority-medium';
import {
  CreateWorkPackagePriorityRequest,
  UpdateWorkPackagePriorityRequest,
  WorkPackagePriorityDto,
  WorkPackagePriorityDtoPage,
} from '@api/backend-connector/models';
import { WorkPackagePrioritiesApi } from '@api/backend-connector/services';

export const WPPriorityIconOptions = [
  wpPriorityHighestIcon,
  wpPriorityHighIcon,
  wpPriorityMediumIcon,
  wpPriorityLowIcon,
  wpPriorityLowestIcon,
].map((icon) => icon.name);

const toasts = {
  createSuccessSummary: 'i18n.WorkPackagePriorityToast.createSuccessSummary',
  createSuccessDetail: 'i18n.WorkPackagePriorityToast.createSuccessDetail',
  createFailSummary: 'i18n.WorkPackagePriorityToast.createFailSummary',
  createFailDetail: 'i18n.WorkPackagePriorityToast.createFailDetail',

  updateSuccessSummary: 'i18n.Toast.updateSuccessSummary',
  updateSuccessDetail: 'i18n.Toast.updateSuccessDetail',
  updateFailSummary: 'i18n.Toast.updateFailSummary',
  updateFailDetail: 'i18n.Toast.updateFailDetail',

  deleteSuccessSummary: 'i18n.WorkPackagePriorityToast.deleteSuccessSummary',
  deleteSuccessDetail: 'i18n.WorkPackagePriorityToast.deleteSuccessDetail',
  deleteFailSummary: 'i18n.WorkPackagePriorityToast.deleteFailSummary',
  deleteFailDetail: 'i18n.WorkPackagePriorityToast.deleteFailDetail',
};

@Injectable({
  providedIn: 'root',
})
export class WorkPackagePrioritiesService extends BaseService {
  private readonly workPackagePriorityApi = inject(WorkPackagePrioritiesApi);
  readonly toasts = toasts;

  readonly refresh$ = new BehaviorSubject<void>(undefined);

  listWorkPackagePriorities(params: ListWorkPackagePriorities$Params): Observable<WorkPackagePriorityDtoPage> {
    return this.workPackagePriorityApi.listWorkPackagePriorities(params);
  }

  private readonly allWorkPackagePriorities$ = this.refresh$
    .pipe(switchMap(() => this.listWorkPackagePriorities({ sorting: ['position'] })))
    .pipe(
      map(({ items }) => items),
      shareReplay({ refCount: false, bufferSize: 1 }),
    );

  allWorkPackagePriorities() {
    return this.allWorkPackagePriorities$;
  }

  getWorkPackagePriority(params: { id: string }): Observable<WorkPackagePriorityDto> {
    return this.workPackagePriorityApi.getWorkPackagePriority({
      ...params,
    });
  }

  createWorkPackagePriority(request: CreateWorkPackagePriorityRequest) {
    return this.workPackagePriorityApi.createWorkPackagePriority({ body: request });
  }

  updatePriority(id: string, body: UpdateWorkPackagePriorityRequest) {
    return this.workPackagePriorityApi.updateWorkPackagePriority({ id, body });
  }

  deleteWorkPackagePriority(params: { id: string }) {
    return this.workPackagePriorityApi.deleteWorkPackagePriority(params);
  }
}
